import { useMemo, useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card } from '../components/primitives';
import { api } from '../lib/api';
import type { DataPoint } from '../../shared/types';

const GROUP_ICON: Record<string, string> = {
  Roles: 'badge',
  Skills: 'workspace_premium',
  'Locations / regions': 'public',
  Industries: 'domain',
  'Project types': 'category',
  'Invoice statuses': 'sell',
  'Contract templates': 'description',
};

export function DataPoints() {
  const { refresh } = useApp();
  const { data } = useFetch<DataPoint[]>('/datapoints');
  const points = data || [];
  const [adding, setAdding] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const groups = useMemo(() => {
    const m: Record<string, DataPoint[]> = {};
    points.forEach((p) => (m[p.group] ||= []).push(p));
    return m;
  }, [points]);

  const add = async (group: string) => {
    const v = draft.trim();
    if (v) await api.post('/datapoints', { group, value: v });
    setDraft('');
    setAdding(null);
    refresh();
  };
  const remove = async (id: number) => {
    await api.del(`/datapoints/${id}`);
    refresh();
  };

  return (
    <Fade>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
        {Object.entries(groups).map(([name, items]) => (
          <Card key={name} style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <Icon name={GROUP_ICON[name] || 'tune'} size={20} color="var(--text-2)" />
              <div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
              <span className="cc-mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{items.length}</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => { setAdding(name); setDraft(''); }} style={{ appearance: 'none', border: 0, background: 'transparent', color: 'var(--accent)', fontFamily: 'inherit', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Icon name="add" size={16} />Add
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {items.map((it) => (
                <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 500, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', padding: '5px 6px 5px 11px', borderRadius: 7 }}>
                  {it.value}
                  <span onClick={() => remove(it.id)} className="cc-clickable" style={{ display: 'inline-flex' }}><Icon name="close" size={15} color="var(--text-3)" /></span>
                </span>
              ))}
              {adding === name && (
                <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(name); if (e.key === 'Escape') setAdding(null); }} onBlur={() => add(name)} placeholder="New value…" style={{ border: '1px solid var(--accent-border)', background: 'var(--surface)', borderRadius: 7, padding: '5px 10px', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--text)', outline: 'none', width: 130 }} />
              )}
            </div>
          </Card>
        ))}
      </div>
    </Fade>
  );
}
